import { onBeforeUnmount, onMounted, provide, ref, watch, type ComponentPublicInstance } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useQueryClient } from '@tanstack/vue-query';
import { useInternational } from '@/common/locale';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import type BaseInput from '@/common/components/molecules/input/BaseInput.vue';
import {
  useMenuSearchState,
  useMenuSearchFilter,
  useMenuSearchWindowSize,
} from './menuSearch.uses';
import { useFavoriteDashboardsMutation, useMenuSearchDefault } from './menuSearch.service';
import { MENU_SEARCH_SHARED_DATA_KEY, MENU_SEARCH_STATE_KEY } from './menuSearch.define';
import { getGetV8DashboardsRecentVisitDashboardsQueryKey } from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';
import { useDashboardListStore } from '@/dashboard/stores/dashboard-list';

export interface Props {
  isShow: boolean;
}

export interface Emits {
  (e: 'update:isShow', value: boolean): void;
}

export const setup = () => {
  const { t } = useInternational();

  const route = useRoute();
  const queryClient = useQueryClient();
  const baseMenuStore = useBaseMenuStore();
  const { isShowMenuSearch: isShow } = storeToRefs(baseMenuStore);

  const sharedData = useMenuSearchDefault();
  provide(MENU_SEARCH_SHARED_DATA_KEY, sharedData);

  const sharedState = useMenuSearchState();
  provide(MENU_SEARCH_STATE_KEY, sharedState);

  const { searchWord, resetMenuSearch } = useMenuSearchFilter(sharedData, sharedState);
  const { resetClickDashboard } = useFavoriteDashboardsMutation();
  const {
    contentRef,
    windowHeightValue,
    resizeObserver,

    calculateWindowHeight,
    getContentElement,
  } = useMenuSearchWindowSize(sharedData, sharedState);

  const { refreshTrigger } = useDashboardListStore();

  // BaseInput ref
  const inputRef = ref<ComponentPublicInstance<typeof BaseInput> | null>(null);

  const closeMenuSearch = () => {
    isShow.value = false;
  };

  const handleSearchInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    searchWord.value = target.value;
  };

  const dashboardRefreshTrigger = () => {
    // 대시보드 목록 화면일 때만 refreshTrigger 호출
    if (route.path.includes('/dashboard/list')) {
      refreshTrigger();
    }

    // 홈 화면일 때 최근 방문 대시보드 목록 새로고침
    if (route.path.includes('/home')) {
      queryClient.invalidateQueries({
        queryKey: getGetV8DashboardsRecentVisitDashboardsQueryKey(),
      });
    }
  };

  // window가 열릴 때/닫힐 때 처리
  watch(isShow, (newVal) => {
    if (newVal) {
      calculateWindowHeight();
      requestAnimationFrame(() => {
        inputRef.value?.focus();
      });
    } else {
      const element = getContentElement();
      if (resizeObserver.value && element) {
        resizeObserver.value.unobserve(element);
        resizeObserver.value = null;
      }
      resetMenuSearch();
      dashboardRefreshTrigger();
      resetClickDashboard();
    }
  });

  const handleEscKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !isShow.value) return;
    e.stopImmediatePropagation();
    isShow.value = false;
  };

  onMounted(() => {
    document.addEventListener('keydown', handleEscKey);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleEscKey);
  });

  return {
    t,
    isShow,
    searchWord,

    contentRef,
    inputRef,
    windowHeightValue,

    closeMenuSearch,
    handleSearchInput,
  };
};
